<template>
    <div>
        <fe-crud
            :config="$models[model]"
            ref="crud"
            @read="handleRead"
        />

        <div v-if="display === 'list'">
        </div>

        <div v-else>
            <v-form v-model="valid" @submit="onSubmit">
                <div v-if="title" class="page-title">{{ title }} </div>
                <div class="d-flex flex-wrap">
                    <template v-for="(field, i) in fieldConfig">
                        <div v-if="field.toggle ? field.toggle(item) : true" :style="{width: field.width ? field.width : null}" class="pr-2" :key="`model-form-`+i">
                            <fe-label v-if="field.label" :id="dataTest+'-radio-label-'+i">{{field.label}}</fe-label>
                            <v-radio-group v-if="field.component === 'v-radio-group'" 
                                v-model="item[field.field]" 
                                class="model-form-radio-group"
                                v-bind="field.attrs"
                                :rules="$fieldValidators(field.validatorKey, field.label, field.validatorConfig)"
                                :data-test="dataTest+'-model-form-radio-group'"
                                :aria-labelledby="dataTest+'-radio-label-'+i"
                            >
                                <v-radio v-for="radio in field.items" :label="radio.name" :value="radio.id" :key="radio.id" :data-test="dataTest+'-model-form-radio-'+radio.id"/>
                            </v-radio-group>
                            <component v-else
                                :is="field.component"
                                v-bind="field.attrs"
                                v-model="item[field.field]"
                                :rules="$fieldValidators(field.validatorKey, field.label, field.validatorConfig)"
                                :aria-labelledby="field.label ? dataTest+'-radio-label-'+i : null"
                                v-on="generateEvents(field, item)"
                            />
                        </div>
                    </template>
                </div>
                <slot/>
            </v-form>

            <div class="d-flex">
                <div class="record-counter" v-if="display == 'page' && items.length>1">Record {{activeIndex+1}} of {{items.length}}</div>

                <div>
                    <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                    ></v-progress-circular>
                </div>
                <fe-btn usage="ghost" class="ml-auto" @click="$emit('cancel')" :data-test="dataTest + '-cancel-btn'">{{cancelButtonText}}</fe-btn>
                <fe-btn v-if="secondaryButtonText" usage="secondary" @click="doSave(true)" :disabled="!valid" :data-test="dataTest + '-secondary-btn'">{{secondaryButtonText}}</fe-btn>
                <fe-btn usage="primary" @click="doSave(false)" :disabled="!valid" :data-test="dataTest + '-next-btn'">{{activeIndex+1 === items.length ? submitButtonText : 'Next'}}</fe-btn>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'ModelForm',
        props: {
            display: {
                type: String,
                default: 'page'
            },
            title: {
                type: String
            },
            model: {
                type: String,
                required: true
            },
            idProperty: {
                type: String,
                required: true
            },
            records: {
                type: Array,
                required: true
            },
            readParams: {

            },
            autoload: {
                type: Boolean,
                default: false
            },
            fieldConfig: {
                type: Array,
                required: true
                /*
                Example:
                {
                    field: 'student_full_name',
                    fieldLabel: 'Student',
                    component: 'v-textfield',
                    validatorKey: 'text',
                    validatorConfig: { limit: 255 },
                    attrs: {}
                }
                */
            },
            submitButtonText: {
                type: String,
                default: 'Save'
            },
            secondaryButtonText: {
                type: String,
                default: null
            },
            cancelButtonText: {
                type: String,
                default: 'Cancel'
            },
            submitPrevent: {
                type: Boolean,
                default: false
            },
            savePrevent: {
                type: Boolean,
                default: false
            },
            dataTest: {
                type: String,
                default: null
            }
        },
        watch: {
            activeIndex(v) {
                this.item = this.items[v]
            },
            records: {
                handler(v) {
                    if (this.items.length === 0) {
                        this.items = v.map(rec => { return {...rec} })

                        this.activeIndex = 0

                        if (v.length > 0) {
                            this.item = v[0]
                        }
                    } else {
                        for (let rec of v) {
                            let itemIndex = this.items.findIndex(itm => itm[this.idProperty] == rec[this.idProperty])
                            if (itemIndex >= 0) {
                                this.items.splice(itemIndex, 1, { ...this.items[itemIndex], ...rec })
                            } else {
                                this.items.push({ ...rec })
                            }
                        }

                        this.activeIndex = 0
                        this.item = this.items[0]
                    }
                },
                deep: true
            }
        },
        data() {
            return {
                valid: false,
                items: [],
                item: {},
                activeIndex: 0,
                loading: false
            }
        },
        mounted() {
            if (this.records) {
                this.items = this.records.map(rec => { return {...rec} })
                if (this.items.length > 0) {
                    this.item = this.items[0]
                }
            }

            if (this.autoload) {
                this.loadData()
            }

        },
        methods: {
            generateEvents(field, item) {
                let me = this

                if (!field.events) return {}

                return Object.entries(field.events).reduce((acc, [eventName, methodName]) => {
                    if (!acc) acc = {}
                    acc[eventName] = function() {
                        methodName(field, item)
                        me.$forceUpdate()
                    }
                    return acc;
                }, {})
            },
            doSave(action=false) {
                if (this.savePrevent) {
                    if (action) {
                        this.$emit('action')
                    }
                    this.$emit('save', this.items)
                    return
                }

                let rec = this.items[this.activeIndex]
                let orig = this.records[this.activeIndex]
                let dirty = false

                this.fieldConfig.forEach(cfg => {
                    if (rec[cfg.field] != orig[cfg.field]) dirty = true
                })

                if (!dirty) {
                    this.continueToNext()
                    return
                }

                if (rec[this.idProperty]) {
                    // Updating
                    this.loading = true
                    this.$refs.crud.update([rec])
                        .then(response => {
                            this.continueToNext()
                        })
                        .finally(() => this.loading = false)
                } else {
                    this.loading = true
                    // Creating
                    this.$refs.crud.create([rec])
                        .then(response => {
                            this.continueToNext()
                        })
                        .finally(() => this.loading = false)
                }
            },
            continueToNext() {
                if ((this.activeIndex+1) < this.items.length) {
                    this.activeIndex++
                } else if (this.activeIndex+1 === this.items.length) {
                    this.$emit('done')
                }
            },
            handleRead(v) {
                this.items = v.length > 0 ? v : [{}]

                this.item = this.items[0]
            },
            loadData() {
                this.$refs.crud.read(this.readParams ? this.readParams : null)
                    .then(response => {})
            },
            onSubmit(e) {
                if (this.submitPrevent) {
                    e.preventDefault();
                }
            },
        }
    }
</script>

<style lang="scss" scoped>
.record-counter {
    line-height: 36px;
    font-size: 12px;

}

.page-counter {
    cursor: pointer;
    height: 26px;
    width: 26px;
    border: 1px solid #1976D2;
    color: #1976D2;
    line-height: 24px;
    margin: 3px;
    text-align: center;
    border-radius: 20px;
    padding-left: 1px;

    &-active {
        background-color: #1976D2;
        color: white;
    }
}

.model-form-radio-group {
    ::v-deep .v-input__slot {
        padding: 0 !important;
    }
}
</style>
